/*
This controller allows you to sort items inside a container by dragging and dropping them.
It sends a POST request to the server with the new order of the items.

The general setup:

<div
  data-controller="sortable"
  data-sortable-url="URL_TO_SEND_UPDATE_TO_AFTER_EACH_DRAG_AND_DROP">
  data-container-type="TYPE_OF_CONTAINER">

  <div
    data-id="THE_ID_OF_THE_ITEM"
    data-action="dragstart->sortable#dragStart dragend->sortable#dragEnd dragover->sortable#dragOver"
    draggable="true">...</div>
  
  ...

</div>

The URL will be called with a parameter `order` that has the IDs of the items in the new order.
*/

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {

  // We want to support dragging an item not just within a single container but also from one container to another.
  // Since each container has its own controller instance, we need to store the dragged item in a static variable.
  static draggedItem;

  // Remember the most recent container that was dragged over.
  static targetContainer;

  connect() {
    // Set up MutationObserver to watch for child changes
    this.setupMutationObserver();

    // Initialize order on connect if we're in skip-submission mode
    const skipSubmission = this.element.getAttribute("data-sortable-skip-submission") === "true";
    if (skipSubmission) {
      this.updateFormFieldOrder(this.element);
    }
  }

  disconnect() {
    // Clean up the observer when the controller is disconnected
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  setupMutationObserver() {
    // Only set up observer if we're in skip-submission mode
    const skipSubmission = this.element.getAttribute("data-sortable-skip-submission") === "true";
    if (!skipSubmission) {
      return;
    }

    // Create observer to watch for child node changes
    this.mutationObserver = new MutationObserver((mutations) => {
      let shouldUpdateOrder = false;

      mutations.forEach((mutation) => {
        // Check if any child nodes were added or removed
        if (mutation.type === 'childList' &&
          (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0)) {

          // Only care about element nodes with data-id (ignore text nodes, hidden inputs, etc.)
          const hasRelevantChanges =
            Array.from(mutation.addedNodes).some(node =>
              node.nodeType === Node.ELEMENT_NODE && node.dataset && node.dataset.id
            ) ||
            Array.from(mutation.removedNodes).some(node =>
              node.nodeType === Node.ELEMENT_NODE && node.dataset && node.dataset.id
            );

          if (hasRelevantChanges) {
            shouldUpdateOrder = true;
          }
        }
      });

      if (shouldUpdateOrder) {
        // Use setTimeout to ensure DOM is fully updated
        setTimeout(() => {
          this.updateFormFieldOrder(this.element);
        }, 0);
      }
    });

    // Start observing
    this.mutationObserver.observe(this.element, {
      childList: true,  // Watch for child additions/removals
      subtree: false    // Only watch direct children, not nested changes
    });
  }

  dragStart(event) {
    event.target.classList.add("opacity-50");
    this.constructor.draggedItem = event.target;
  }

  dragOver(event) {
    event.preventDefault(); // Allow the drop

    const container = this.getClosestContainer(event.clientY);

    if (container) {
      this.constructor.targetContainer = container;
    } else {
      return;
    }

    const afterElement = this.getDragAfterElement(container, event.clientY);

    if (afterElement == null) {
      container.appendChild(this.constructor.draggedItem);
    } else {
      container.insertBefore(this.constructor.draggedItem, afterElement);
    }
  }

  dragEnd(event) {
    const container = this.getClosestContainer(event.clientY);

    if (container) {
      this.constructor.targetContainer = container;
    }

    if (this.constructor.targetContainer == null) {
      return;
    }
    this.saveOrder(this.constructor.targetContainer);

    event.target.classList.remove("opacity-50");
    this.constructor.draggedItem = null;
    this.constructor.targetContainer = null;
  }

  getClosestContainer(y) {
    const sourceContainerType = this.constructor.draggedItem.parentElement.getAttribute("data-container-type");
    const containers = document.querySelectorAll('[data-controller="sortable"][data-container-type="' + sourceContainerType + '"]');

    var selectedContainer = null;

    // First look for a container that entirely contains the dragged item's y-coordinate.
    containers.forEach(container => {
      const box = container.getBoundingClientRect();
      if (y >= box.top && y <= box.bottom) {
        selectedContainer = container;
      }
    });

    if (selectedContainer) {
      return selectedContainer;
    }

    // If that fails, find the container whose center is closest to the dragged item's y-coordinate.
    // This is important for situations where the target container is empty, meaning it has a height
    // of zero, so the dragged item's y-coordinate will not be inside the container.
    var closestDistance = Number.POSITIVE_INFINITY;

    containers.forEach(container => {
      const box = container.getBoundingClientRect();
      const containerCenter = box.top + box.height / 2;
      const distance = Math.abs(y - containerCenter);

      if (distance < closestDistance) {
        selectedContainer = container;
        closestDistance = distance;
      }
    });

    return selectedContainer;
  }

  getDragAfterElement(container, y) {
    const draggableElements = [...container.querySelectorAll("[draggable]:not(.opacity-50)")];

    return draggableElements.reduce((closest, child) => {
      const box = child.getBoundingClientRect();
      const offset = y - box.top - box.height / 2;

      if (offset < 0 && offset > closest.offset) {
        return { offset: offset, element: child };
      } else {
        return closest;
      }
    }, { offset: Number.NEGATIVE_INFINITY }).element;
  }

  async saveOrder(container) {
    const url = container.getAttribute("data-sortable-url");
    const skipSubmission = container.getAttribute("data-sortable-skip-submission") === "true";

    if (skipSubmission) {
      // Update form field names to match new order
      this.updateFormFieldOrder(container);

      // Just trigger a custom event that other controllers can listen for
      const event = new CustomEvent('sortable:orderChanged', {
        detail: {
          container: container,
          order: Array.from(container.children).map(child => child.dataset.id)
        }
      });
      container.dispatchEvent(event);
      return;
    }

    const idsInOrder = Array.from(container.children).map(child => child.dataset.id);

    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ order: idsInOrder })
    });
  }

  updateFormFieldOrder(container) {
    // Get the order input name from data attribute
    const orderInputName = container.getAttribute("data-sortable-order-input");
    if (!orderInputName) {
      return; // No order input specified, skip
    }

    // Clear any existing order inputs with this name
    const existingOrderInputs = container.querySelectorAll(`input[name="${orderInputName}"]`);
    existingOrderInputs.forEach(input => input.remove());

    // Create hidden inputs to preserve the drag-and-drop order
    const items = Array.from(container.children).filter(item => item.dataset.id);
    items.forEach((item, index) => {
      const dataId = item.dataset.id;

      // Create a hidden input to preserve the order
      const orderInput = document.createElement('input');
      orderInput.type = 'hidden';
      orderInput.name = orderInputName;
      orderInput.value = dataId;

      // Append to the container (these will be processed server-side)
      container.appendChild(orderInput);
    });
  }
}